import React, { useEffect, useRef } from "react";
import imageSize, { responsive } from "./image-size";
import LazyVideo from "./lazy-video";
import ProjectArt from "./project-art";
import ProjectUI from "./project-ui";

/* A product's artwork, laid out on a fixed 520 px design square. The orbit
   circles, the click transition and the case-study images all show the
   same face, so a product looks the same everywhere it moves. */

export const FACE_SIZE = 520;

/* Sizes a face to cover a W x H box: the shorter side of the box maps to
   the design square (times zoom), and the face box stretches along the
   longer side so cover media fill the whole frame while art stays centered.
   Cover photos and videos fill the face box at any scale, so their zoom
   is applied to the media itself through --media-zoom. */
export function fitFace(face, width, height, zoom = 1) {
  if (!face || width <= 0 || height <= 0) return;
  const scale = (Math.min(width, height) / FACE_SIZE) * zoom;
  face.style.width = `${width / scale}px`;
  face.style.height = `${height / scale}px`;
  face.style.transform = `scale(${scale})`;
  face.style.setProperty("--media-zoom", String(zoom));
}

/* `sizes` tells the browser how wide this face is drawn (it is laid out at
   520 px and scaled with a transform, so it cannot tell on its own). */
export function ProductFace({ product: source, reducedMotion = false, sizes = "(max-width: 700px) 90vw, 50vw" }) {
  // A product's `headerImage` is its one picture everywhere: the orbit, the
  // click transition, the case-study header, and the other cards.
  const product = source.headerImage
    ? { ...source, image: source.headerImage, art: false, video: null, ui: null, compact: false, flush: false, portrait: false }
    : source;
  let media = null;
  if (product.art) {
    media = <ProjectArt project={product.slug} />;
  } else if (product.video) {
    media = (
      <LazyVideo
        className="project-media project-media--cover"
        src={product.video}
        poster={product.video.replace(/^\/(.*)\.mp4$/, "/posters/$1.webp")}
        reducedMotion={reducedMotion}
      />
    );
  } else if (product.image) {
    // A landscape image is a cover: it fills the frame instead of sitting
    // at the bottom.
    const size = imageSize(product.image);
    const landscape = size.width && size.width / size.height >= 1.3;
    media = (
      <img
        className={`project-media${landscape ? " project-media--cover" : ""}${product.portrait ? " project-media--portrait" : ""}${
          product.compact ? " project-media--compact" : ""
        }${product.flush ? " project-media--flush" : ""}`}
        src={product.image}
        {...imageSize(product.image)}
        {...responsive(product.image, sizes)}
        alt=""
        loading="lazy"
        decoding="async"
      />
    );
  } else if (product.ui) {
    media = <ProjectUI project={product} />;
  }

  return (
    <div
      className={`orb-face project-card project-card--${product.tone} project-card--${product.slug}${
        product.art ? " project-card--art" : ""
      }${product.video ? " orb-face--video" : ""}${product.image ? " orb-face--image" : ""}`}
      style={{ "--card-color": product.color }}
    >
      {media}
    </div>
  );
}

/* A frame of any shape that keeps its face fitted as it resizes. */
/* parallax: { inner: speed } or { hero: speed } marks the image layer for
   useParallax(); the frame itself stays put and clips it. */
export function FittedFace({ product, zoom = 1, origin = "50% 50%", className = "", children, frameRef, parallax, sizes }) {
  const ownRef = useRef(null);
  const ref = frameRef || ownRef;

  useEffect(() => {
    const frame = ref.current;
    if (!frame) return undefined;
    const face = frame.querySelector(".orb-face");
    const fit = () => fitFace(face, frame.clientWidth, frame.clientHeight, zoom);
    fit();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", fit);
      return () => window.removeEventListener("resize", fit);
    }
    const observer = new ResizeObserver(fit);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [ref, zoom]);

  return (
    <div
      ref={ref}
      className={`face-frame ${className}`}
      style={{ "--card-color": product.color, "--media-origin": origin }}
    >
      <div
        aria-hidden="true"
        className="face-shift"
        data-parallax-inner={parallax?.inner}
        data-parallax-hero={parallax?.hero}
      >
        <ProductFace product={product} sizes={sizes} />
      </div>
      {children}
    </div>
  );
}
